/**
 * EN-1253: per-document editor sync state that must outlive a single EditorProvider instance.
 * A tab switch remounts the editor (PatientView `key={activeTabId}`) and a version switch reuses
 * the instance with a new chatId, so base/baseline and the save queue are keyed by chatId here
 * rather than held in component refs. A save captured for one document can therefore never read
 * another document's base.
 */
import { ChatJson } from "@/common/utils/types";
import { ChartConfig } from "@/common/config/charts/dental";
import { ChartPatches } from "../buildChatJson";

export interface SyncEntry {
    base: ChatJson;
    baseline: ChatJson;
    /**
     * What the editor's children were last reset from by resetFromServer or adoptLocalCopy. Server
     * content, or a client-format mine (latestMine, an adopted copy) when a reset kept unsent work.
     * resetDisplayKeepingPair and resetForDisplay replace the children without changing it (Ruling
     * 8), so it can lag the editor. buildMine builds on it.
     */
    display: ChatJson;
    /**
     * EN-1253 Ruling 8: the newest `mine` built for this document (at each edit and each save
     * capture). A remount with pending work shows this, never Redux, which may hold a stale
     * server result or another device's restructure. A reset with no unsent work sets it to the
     * baseline; one with unsent work keeps it; adoptLocalCopy sets it to the copy's mine.
     */
    latestMine: ChatJson;
    chartConfig: ChartConfig | undefined;
    chartPatches: ChartPatches;
    rev: number;
    savedRev: number;
    lastSaveCompletedAt: number;
    /** When `display` last changed; with `display` identity, a save's reset generation (Ruling 5). */
    lastResetAt: number;
    /**
     * An optimistic mine was dispatched to Redux and no server result has replaced it yet (its save
     * failed or is in flight). While true, a save whose mine matches the baseline is still sent,
     * so its result replaces the optimistic content in Redux (otherwise Redux keeps an abandoned
     * edit, which the header effect and showReduxCopy then trust). It also stays true after a
     * conflict, and across resets. A replay never sets it.
     */
    unconfirmedOptimistic: boolean;
    /**
     * A "Use the other version" result that landed when its reset could not run (the sending
     * instance had unmounted or shows another document, or a Scout proposal or draft owned the
     * editor), with nothing typed since the click. The update that records it notifies, so an
     * instance showing the document that can reset does so at once; otherwise it resets at the
     * first change after it can (spec 2.4), unless typing has started by then, which drops it. A
     * full reset (initSyncEntry, such as a later mount re-basing on Redux) clears it unused.
     */
    owedTheirsReset?: ChatJson;
    /**
     * Set when a section regenerate ended with an unsent edit and the editor kept the pair: the
     * editor shows that edit without the regenerated section. The next save to land resets the
     * editor to its result (which holds both) if nothing was typed since it was captured; any
     * landing clears it. A full reset (initSyncEntry) clears it unused.
     */
    resetOnLand?: boolean;
}

const entries = new Map<string, SyncEntry>();
const queues = new Map<string, Promise<void>>();
const active = new Map<string, number>();
const listeners = new Map<string, Set<() => void>>();
const regenerates = new Map<string, { inFlight: number; settled: number }>();

/**
 * EN-1253 Ruling 16: listen for changes to one document's entry. A remounted editor uses this to
 * learn that a save started by an earlier instance has landed. Returns the unsubscribe function.
 */
export function subscribe(chatId: string, listener: () => void): () => void {
    const set = listeners.get(chatId) ?? new Set<() => void>();
    set.add(listener);
    listeners.set(chatId, set);
    return () => {
        set.delete(listener);
        if (set.size === 0 && listeners.get(chatId) === set) listeners.delete(chatId);
    };
}

function notify(chatId: string): void {
    listeners.get(chatId)?.forEach((listener) => listener());
}

export const getSyncEntry = (chatId: string): SyncEntry | undefined => entries.get(chatId);

export function initSyncEntry(
    chatId: string,
    init: Pick<SyncEntry, "base" | "baseline" | "display" | "chartConfig">,
): SyncEntry {
    const prev = entries.get(chatId);
    const entry: SyncEntry = {
        ...init,
        latestMine: init.baseline,
        chartPatches: {},
        rev: prev?.rev ?? 0,
        savedRev: prev?.savedRev ?? 0,
        lastSaveCompletedAt: prev?.lastSaveCompletedAt ?? 0,
        lastResetAt: Date.now(),
        unconfirmedOptimistic: prev?.unconfirmedOptimistic ?? false,
        owedTheirsReset: undefined,
        resetOnLand: undefined,
    };
    entries.set(chatId, entry);
    notify(chatId);
    return entry;
}

export function updateSyncEntry(chatId: string, patch: Partial<SyncEntry>): SyncEntry | undefined {
    const prev = entries.get(chatId);
    if (!prev) return undefined;
    const next = { ...prev, ...patch };
    entries.set(chatId, next);
    notify(chatId);
    return next;
}

export function enqueueForDocument<T>(chatId: string, task: () => Promise<T>): Promise<T> {
    active.set(chatId, (active.get(chatId) ?? 0) + 1);
    const prev = queues.get(chatId) ?? Promise.resolve();
    const run = prev.then(task);
    const tail = run
        .then(
            () => undefined,
            () => undefined,
        )
        .finally(() => {
            const n = (active.get(chatId) ?? 1) - 1;
            if (n <= 0) active.delete(chatId);
            else active.set(chatId, n);
        });
    queues.set(chatId, tail);
    return run;
}

export const isDocumentSaveActive = (chatId: string): boolean => (active.get(chatId) ?? 0) > 0;

/**
 * EN-1253 Ruling 26: section regenerate POSTs per document, kept here so a remount during a
 * regenerate still knows one is in flight. The server writes status completed only when it
 * finishes and never marks the row regenerating, so a read taken while a POST is in flight shows
 * the old text as completed and must not end the Regenerating state.
 */
export function markRegenerateStarted(chatId: string): void {
    const r = regenerates.get(chatId) ?? { inFlight: 0, settled: 0 };
    regenerates.set(chatId, { ...r, inFlight: r.inFlight + 1 });
}

export function markRegenerateSettled(chatId: string): void {
    const r = regenerates.get(chatId) ?? { inFlight: 1, settled: 0 };
    regenerates.set(chatId, { inFlight: Math.max(0, r.inFlight - 1), settled: r.settled + 1 });
}

/**
 * Null while a regenerate POST for this document is in flight; otherwise how many have settled.
 * A read may end a Regenerating state only if this is non-null and unchanged across the read.
 */
export function regenerateSettledToken(chatId: string): number | null {
    const r = regenerates.get(chatId);
    if (!r) return 0;
    return r.inFlight > 0 ? null : r.settled;
}

export function resetDocumentSyncForTests(): void {
    regenerates.clear();
    entries.clear();
    queues.clear();
    active.clear();
    listeners.clear();
}
